/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ListInputProps } from '../../../../types/InputComponentProps';
import { HStack, Input } from 'chakra-v3';
import { ChangeEventHandler, KeyboardEventHandler, useState } from 'react';
import { ListItem } from './ListItem';
import { InputContainer } from '../FormInputContainer';

export function ListInput(props: ListInputProps) {
  const {
    description,
    value,
    onChange,
    id,
    label,
    error,
    isInvalid,
    isRequired,
    isDisabled,
    delimiters,
    size: _size,
    ...rest
  } = props;
  const [inputValue, setInputValue] = useState('');
  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
  };
  const handleAddTag: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || delimiters?.includes(e.key)) {
      e.preventDefault();
      if (inputValue) {
        onChange(id!, [...value, inputValue.trim()]);
        setInputValue('');
      }
    }
  };
  const handleRemoveTag = (tagToRemove: string) => {
    const updatedList = value.filter((tag) => tag !== tagToRemove);
    onChange(id!, updatedList);
  };
  return (
    <InputContainer
      id={id}
      description={description}
      label={label}
      isRequired={isRequired}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      error={error}
    >
      <Input
        id={id}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleAddTag}
        disabled={isDisabled}
        bg="brand.tealDark"
        borderColor="brand.cta"
        color={inputValue ? 'text.primary' : 'text.placeholder'}
        h="48px"
        px="16px"
        py="12px"
        borderRadius="4px"
        fontSize="16px"
        lineHeight="1.4"
        letterSpacing="0.08px"
        fontFamily="body"
        fontWeight="normal"
        _placeholder={{ color: 'text.placeholder' }}
        {...rest}
      />
      <HStack flexWrap="wrap" width="100%" gap={2}>
        {value.map((label, index) => (
          <ListItem label={label} handleRemove={handleRemoveTag} key={index} isDisabled={isDisabled} />
        ))}
      </HStack>
    </InputContainer>
  );
}
